/**
 * File autocomplete dropdown for @-mentions in chat input.
 *
 * Shows filtered list of workspace files when user types @ prefix.
 * Supports keyboard navigation and mouse selection.
 */

import React, { useEffect, useRef } from 'react';
import { FileIcon } from '../files/FileIcon';
import type { FileInfo } from '../../hooks/useFileList';

interface FileAutocompleteProps {
  files: FileInfo[];
  query: string;
  selectedIndex: number;
  onSelect: (file: FileInfo) => void;
  onClose: () => void;
  position?: { top: number; left: number };
}

export function FileAutocomplete({
  files,
  query,
  selectedIndex,
  onSelect,
  onClose,
  position,
}: FileAutocompleteProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Filter files based on query
  const filteredFiles = files.filter((file) =>
    file.name.toLowerCase().includes(query.toLowerCase())
  );

  // Scroll selected item into view
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (filteredFiles.length === 0) {
    return (
      <div
        ref={listRef}
        className="absolute z-50 w-64 max-h-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        style={position ? { bottom: position.top, left: position.left } : { bottom: '100%', left: 0 }}
        data-testid="file-autocomplete"
      >
        <div className="px-3 py-2 text-sm text-gray-500">
          {query ? `No files matching "${query}"` : 'No files in workspace'}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="absolute z-50 w-72 max-h-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto"
      style={position ? { bottom: position.top, left: position.left } : { bottom: '100%', left: 0 }}
      data-testid="file-autocomplete"
    >
      <div className="py-1">
        {filteredFiles.map((file, index) => (
          <button
            key={file.path}
            ref={index === selectedIndex ? selectedRef : null}
            type="button"
            onClick={() => onSelect(file)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
              index === selectedIndex
                ? 'bg-sky-50 text-sky-900'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            data-testid={`file-option-${file.name}`}
          >
            <FileIcon filename={file.name} className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{file.name}</span>
            <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
              {formatFileSize(file.size)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default FileAutocomplete;
